/* eslint-disable max-lines-per-function */
import { useCallback, useState } from 'react';

import { CompanyEntry, CompanyEntryQuery, FiscalDocument } from '@dreryk/egabinet-invoice-frontend-api';
import { TaxIdentifier } from '@dreryk/tax-identifier-common-api';

import { performCqrsQuerySearch } from '@dreryk/command-query-react-common';
import { paginations } from '@dreryk/command-query-ws-client';
import { FormContextValues } from '@dreryk/react-common';
import { dispatchSnackbarNegativeEvent, Grid, GridAreas, Heading } from '@dreryk/react-components';

import { TaxIdentifierField } from '../../../components/TaxIdentifierField';
import { invoiceCreateModalTranslationKeys } from '../../../translations';
import { InvoiceCreateFormAddressSectionBeanPart } from '../addressSection/InvoiceCreateFormAddressSection.types';
import { InvoiceCreateFormRegisterControllerProp } from '../InvoiceCreateForm.types';
import { fiscalDocumentTypeVisitor } from '../InvoiceCreateForm.utils';
import { invoiceCreateFormInvoiceForVisitor } from '../InvoiceCreateFormSwitch.utils';

import { InvoiceCreateFormCompanyIdentificationDetailsFormBeanPart } from './InvoiceCreateFormCompanyIdentificationDetails.types';
import { InvoiceCreateFormCompanyIdentificationDetailsCentralStatisticalOfficeButton } from './InvoiceCreateFormCompanyIdentificationDetailsCentralStatisticalOfficeButton';
import { InvoiceCreateFormCompanyName } from './InvoiceCreateFormCompanyName';
import { InvoiceCreateFormCompanyNameFormBeanPart } from './InvoiceCreateFormCompanyName.types';

type COMPANY_GRID_AREA = 'taxIdentifier' | 'centralStatisticalOfficeButton' | 'companyName';

const COMPANY_GRID_COLUMNS = 3;
const GRID_GAP = '24px 10px';

const COMPANY_GRID_AREAS: GridAreas<COMPANY_GRID_AREA, typeof COMPANY_GRID_COLUMNS> = {
   custom: {
      template: ['taxIdentifier centralStatisticalOfficeButton companyName'],
      gridTemplateColumns: '256px 194px 582px',
      gap: GRID_GAP,
   },
};

type InvoiceCreateFormCompanyIdentificationDetailsProps = Pick<
   FormContextValues<
      InvoiceCreateFormCompanyIdentificationDetailsFormBeanPart & InvoiceCreateFormAddressSectionBeanPart
   >,
   'getValue' | 'getValues' | 'reset' | 'trigger'
> &
   InvoiceCreateFormRegisterControllerProp &
   InvoiceCreateFormCompanyNameFormBeanPart &
   Readonly<{
      fiscalDocuments: FiscalDocument[];
      taxIdentifier: TaxIdentifier | undefined;
   }>;

export const InvoiceCreateFormCompanyIdentificationDetails = (
   props: InvoiceCreateFormCompanyIdentificationDetailsProps
): JSX.Element => {
   const { companyName, getValue, getValues, register, reset, trigger } = props;
   const [companyEntries, setCompanyEntries] = useState<CompanyEntry[]>();

   const setCompanyFormValues = useCallback(
      (entries: CompanyEntry[]): void =>
         invoiceCreateFormInvoiceForVisitor('COMPANY')({
            visitCompany: () => {
               const [defaultEntry] = entries;

               reset({
                  ...getValues(),
                  building: defaultEntry.partialAddress?.findBuilding() ?? null,
                  companyName: entries.length > 1 ? defaultEntry : defaultEntry.name,
                  country: defaultEntry.partialAddress?.getCountry(),
                  flat: defaultEntry.partialAddress?.findFlat() ?? null,
                  postcode: defaultEntry.partialAddress?.findPostcode() ?? null,
                  street: defaultEntry.partialAddress?.findStreet() ?? null,
                  taxIdentifier: defaultEntry.taxIdentifier.toString(),
                  town: defaultEntry.partialAddress?.findTown() ?? null,
               });
            },
            visitPrivateIndividual: () => undefined,
         }),
      [getValues, reset]
   );

   const handleDownloadCompanyEntry = useCallback(
      async (taxId?: TaxIdentifier) => {
         trigger('taxIdentifier');
         const taxIdentifier = taxId && TaxIdentifier.isValid(taxId.toString()) ? taxId : getValue('taxIdentifier');

         if (TaxIdentifier.isValid(taxIdentifier.toString())) {
            try {
               const result = await performCqrsQuerySearch<CompanyEntry>(() => {
                  const query = new CompanyEntryQuery();
                  query.taxIdentifier = taxIdentifier;
                  return query;
               }, paginations.all());

               const entries = result.filter((entry) => !entry.terminationDate);

               if (entries.length > 0) {
                  setCompanyEntries(entries);
                  setCompanyFormValues(entries);
               }

               if (entries.length === 0) {
                  dispatchSnackbarNegativeEvent(
                     invoiceCreateModalTranslationKeys.downloadDataFromCentralStatisticalOfficeError
                  );
               }
            } catch (error) {
               dispatchSnackbarNegativeEvent(
                  invoiceCreateModalTranslationKeys.downloadDataFromCentralStatisticalOfficeError
               );
            }
         }
      },
      [getValue, setCompanyFormValues, trigger]
   );

   return (
      <section>
         <Heading
            content={invoiceCreateModalTranslationKeys.companyDetails}
            testId={'companyDetails'}
            type={'h2'}
            margin={'18px'}
         />

         <Grid<COMPANY_GRID_AREA, typeof COMPANY_GRID_COLUMNS>
            areas={COMPANY_GRID_AREAS}
            testId={'companyGrid'}
            companyName={
               <InvoiceCreateFormCompanyName
                  companyEntries={companyEntries}
                  companyName={companyName}
                  getValues={getValues}
                  register={register}
                  reset={reset}
               />
            }
            centralStatisticalOfficeButton={fiscalDocumentTypeVisitor(props.fiscalDocuments)({
               receiptWithTaxIdentifier: () => (
                  <InvoiceCreateFormCompanyIdentificationDetailsCentralStatisticalOfficeButton
                     onClick={async () =>
                        handleDownloadCompanyEntry(
                           props.fiscalDocuments.find((fiscalDocument) => 'taxIdentifier' in fiscalDocument)
                              ?.taxIdentifier
                        )
                     }
                     triggerClickOnMount
                  />
               ),

               noFiscalDocuments: () => (
                  <InvoiceCreateFormCompanyIdentificationDetailsCentralStatisticalOfficeButton
                     onClick={async () => handleDownloadCompanyEntry(getValue('taxIdentifier') ?? props.taxIdentifier)}
                     triggerClickOnMount={Boolean(props.taxIdentifier)}
                  />
               ),
            })}
            taxIdentifier={
               <TaxIdentifierField
                  register={register}
                  disabled={fiscalDocumentTypeVisitor(props.fiscalDocuments)({
                     receiptWithTaxIdentifier: () => true,
                     noFiscalDocuments: () => false,
                  })}
               />
            }
         />
      </section>
   );
};
